import React, { useState } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  TextField, 
  MenuItem, 
  Select, 
  FormControl, 
  InputLabel,
  Typography,
  Box,
  Snackbar,
  Alert,
  CircularProgress
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';

const FEEDBACK_TYPES = [
  { value: 'feature', label: 'Feature Request' },
  { value: 'bug', label: 'Bug Report' },
  { value: 'question', label: 'Question/Information' }
];

const FeedbackForm = ({ open, onClose }) => {
  const { isAuthenticated, user } = useAuth();
  const [formData, setFormData] = useState({
    type: 'feature',
    subject: '',
    description: '',
    email: '',
    name: ''
  });
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Get API URL from environment variable with fallback
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.subject || !formData.description) {
      setAlert({
        open: true,
        message: 'Please fill in all required fields',
        severity: 'error'
      });
      return;
    }
    
    // For anonymous users, validate email if provided
    if (!isAuthenticated && formData.email && !validateEmail(formData.email)) {
      setAlert({
        open: true,
        message: 'Please enter a valid email address',
        severity: 'error'
      });
      return;
    }
    
    try {
      setLoading(true);
      
      const feedbackData = {
        ...formData,
        email: isAuthenticated ? user?.email : formData.email,
        name: isAuthenticated ? (user?.displayName || user?.name) : formData.name || 'Anonymous User'
      };
      
      console.log('Submitting feedback to:', `${API_URL}/api/feedback`);
      const response = await axios.post(`${API_URL}/api/feedback`, feedbackData);
      
      // Check if the request was successful
      if (response.data && response.data.success) {
        setAlert({
          open: true,
          message: 'Thank you for your feedback! We will review it shortly.',
          severity: 'success'
        });
        
        // Reset form
        setFormData({
          type: 'feature',
          subject: '',
          description: '',
          email: '',
          name: ''
        });
        
        // Close modal after short delay
        setTimeout(() => {
          onClose();
        }, 2000);
      } else {
        throw new Error(response.data?.message || 'Unknown error occurred');
      }
    } catch (error) {
      console.error('Error submitting feedback:', error);
      let errorMessage = 'Failed to submit feedback';
      
      // Extract the most useful error message
      if (error.response) {
        // The server responded with an error
        errorMessage = error.response.data?.message || `Server error: ${error.response.status}`;
      } else if (error.request) {
        // The request was made but no response was received
        errorMessage = 'No response received from server. Please check your connection.';
      } else {
        // Something else happened
        errorMessage = error.message || errorMessage;
      }
      
      setAlert({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const handleCloseAlert = () => {
    setAlert(prev => ({ ...prev, open: false }));
  };

  return (
    <>
      <Dialog 
        open={open} 
        onClose={onClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(45deg, #1a237e, #0d47a1)',
          color: 'white',
          fontWeight: 700,
          py: 2
        }}>
          We Value Your Input
        </DialogTitle>
        <DialogContent sx={{ pt: 3, pb: 2 }}>
          <Box component="form" onSubmit={handleSubmit} noValidate>
            <Typography variant="body1" sx={{ mb: 3, color: '#546e7a' }}>
              Your feedback helps us improve SecurePoint IQ. Please share your thoughts, 
              ideas, or report any issues you've encountered.
            </Typography>

            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel id="feedback-type-label">Feedback Type</InputLabel>
              <Select
                labelId="feedback-type-label"
                id="type"
                name="type"
                value={formData.type}
                onChange={handleChange}
                label="Feedback Type"
              >
                {FEEDBACK_TYPES.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {!isAuthenticated && (
              <>
                <TextField
                  margin="normal"
                  fullWidth
                  id="name"
                  label="Your Name (Optional)"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  sx={{ mb: 2 }}
                />
                
                <TextField
                  margin="normal"
                  fullWidth
                  id="email"
                  label="Your Email (Optional)"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  helperText="Providing your email allows us to follow up with you"
                  sx={{ mb: 2 }}
                />
              </>
            )}

            <TextField
              margin="normal"
              required
              fullWidth
              id="subject"
              label="Subject"
              name="subject"
              value={formData.subject}
              onChange={handleChange}
              sx={{ mb: 2 }}
            />

            <TextField
              margin="normal"
              required
              fullWidth
              multiline
              rows={5}
              id="description"
              label="Description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Please provide details about your feedback..."
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={onClose} 
            color="inherit"
            disabled={loading}
            sx={{ fontWeight: 500 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={loading}
            variant="contained"
            color="primary"
            sx={{ 
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600
            }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Submit Feedback'}
          </Button>
        </DialogActions>
      </Dialog>
      
      <Snackbar 
        open={alert.open} 
        autoHideDuration={6000} 
        onClose={handleCloseAlert}
      >
        <Alert 
          onClose={handleCloseAlert} 
          severity={alert.severity} 
          sx={{ width: '100%' }}
        >
          {alert.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default FeedbackForm; 